import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Clock, StickyNote, Phone, MessageCircle, Mail, MessageSquare, File, Activity,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from './ReportFilters';


const LOG_TYPE_META = {
  note: { label: 'Note', icon: StickyNote, color: 'text-amber-500' },
  call: { label: 'Call', icon: Phone, color: 'text-green-500' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-500' },
  email: { label: 'Email', icon: Mail, color: 'text-blue-500' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'text-cyan-500' },
  document: { label: 'Document', icon: File, color: 'text-purple-500' },
  status: { label: 'Status', icon: Activity, color: 'text-indigo-500' },
  system: { label: 'System', icon: Activity, color: 'text-slate-500' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';


export default function TimelineReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [preset, setPreset] = useState('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // Fetch activity for all enquiries — pragmatic approach
        const eResp = await apiFetch('engage/enquiries/');
        const list = eResp?.results || eResp?.data?.results || [];
        if (cancelled) return;
        setEnquiries(list);

        const allLogs = [];
        // Fetch first 20 enquiries' logs for the demo
        const subset = list.slice(0, 20);
        const results = await Promise.all(subset.map(e => apiFetch(`engage/activity-logs/?enquiry=${e.id}`).catch(() => null)));
        for (let i = 0; i < subset.length; i++) {
          const r = results[i];
          if (r) {
            const rows = r?.data?.results || r?.data || [];
            for (const l of rows) allLogs.push({ ...l, _enquiry_name: subset[i].name });
          }
        }
        allLogs.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
        if (!cancelled) setLogs(allLogs);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load timeline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filtered = useMemo(() => {
    const { from, to } = presetToRange(preset, customFrom, customTo);
    const fromMs = from ? new Date(from + 'T00:00:00').getTime() : -Infinity;
    const toMs   = to   ? new Date(to   + 'T23:59:59').getTime() :  Infinity;
    return logs.filter(l => {
      if (typeFilter !== 'all' && l.log_type !== typeFilter) return false;
      const ms = new Date(l.created_date).getTime();
      if (ms < fromMs || ms > toMs) return false;
      return true;
    });
  }, [logs, typeFilter, preset, customFrom, customTo]);

  const typeOptions = useMemo(() => [
    { value: 'all', label: 'All types' },
    ...Object.entries(LOG_TYPE_META).map(([k, m]) => ({ value: k, label: m.label })),
  ], []);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center text-white shadow-sm">
          <Clock className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Timeline</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Recent activity across all enquiries</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{ label: 'Type', value: typeFilter, onChange: setTypeFilter, options: typeOptions }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="space-y-2">
        {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
        : filtered.length === 0 ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">No activity in this range.</div>
        : filtered.map(l => {
          const M = LOG_TYPE_META[l.log_type] || LOG_TYPE_META.system;
          return (
            <div key={l.id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
              <div className="flex items-start gap-2.5">
                <div className={cn('w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700')}>
                  <M.icon className={cn('w-4 h-4', M.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/engage/enquiries/${l.enquiry}`} className="font-medium text-sm text-blue-600 dark:text-blue-400 hover:underline">
                      {l._enquiry_name || `Enquiry #${l.enquiry}`}
                    </Link>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{fmt(l.created_date)}</span>
                  </div>
                  <div className="mt-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{M.label}</span>
                  </div>
                  {l.message && <p className="text-[13px] text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{l.message}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
